// ./src/components/features/Cars/CarEngineTypeSelector/CarEngineTypeSelector.tsx
import React, { useMemo, useCallback } from "react";
import { Select, SelectProps, Group, Text, Badge, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { CarEngineType } from "@/utils/api";
import { useAppEssentialsContext } from "@/contexts";
import { LoadingIndicator, AppIcon } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";

// ============================================
// Types
// ============================================

export interface CarEngineTypeSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange" | "name"> {
  /** Field name for form binding */
  name?: string;

  /** Currently selected engine type ID */
  value?: number | string | null | undefined;

  /** Callback fired when engine type selection changes: (name, value, engineType?) */
  onChange?: (
    name: string,
    value: number | null,
    engineType?: CarEngineType
  ) => void;

  /** Placeholder text when no engine type is selected */
  placeholder?: string;

  /** Whether to include inactive engine types in the list */
  includeInactive?: boolean;

  /** Custom filter function to filter engine types */
  engineTypeFilter?: (engineType: CarEngineType) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Placeholder text shown while loading */
  loadingPlaceholder?: string;

  /** Text shown when no engine types are available */
  emptyText?: string;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;
}

// ============================================
// Option Data Interface
// ============================================

interface EngineTypeOptionData {
  value: string;
  label: string;
  code?: string;
  iconName: string;
  color: string;
  disabled?: boolean;
}

// ============================================
// Constants - Icon and color mapping for engine types
// ============================================

const ENGINE_TYPE_ICONS: Record<string, string> = {
  GAS: "GasStation",
  GASOLINE: "GasStation",
  DIESEL: "GasStation",
  HYBRID: "BatteryCharging",
  ELECTRIC: "BoltFilled",
  HYDROGEN: "Atom",
  LPG: "GasStation",
  CNG: "GasStation",
};

const ENGINE_TYPE_COLORS: Record<string, string> = {
  ELECTRIC: "green",
  HYBRID: "teal",
  HYDROGEN: "cyan",
  DIESEL: "orange",
  GAS: "gray",
  GASOLINE: "gray",
  LPG: "yellow",
  CNG: "lime",
};

const getEngineTypeIcon = (code: string | undefined): string => {
  if (!code) return "Engine";
  return ENGINE_TYPE_ICONS[code.toUpperCase()] || "Engine";
};

const getEngineTypeColor = (code: string | undefined): string => {
  if (!code) return "gray";
  return ENGINE_TYPE_COLORS[code.toUpperCase()] || "gray";
};

// ============================================
// Component
// ============================================

const CarEngineTypeSelectorComponent: React.FC<CarEngineTypeSelectorProps> = ({
  name = "engineTypeId",
  value,
  onChange,
  placeholder,
  includeInactive = false,
  engineTypeFilter,
  required = false,
  loadingPlaceholder,
  emptyText,
  error,
  disabled,
  size = "md",
  label,
  ...selectProps
}) => {
  const { t } = useTranslation(["cars", "common"]);
  const { appEssentials, isLoading, lastError, getCarEngineTypeById } =
    useAppEssentialsContext();

  const engineTypes = appEssentials?.carEngineTypes || [];

  // Default translations
  const defaultPlaceholder =
    placeholder || t("cars:selectors.engineType.placeholder");
  const defaultLabel = label ?? t("cars:selectors.engineType.label");
  const defaultLoadingPlaceholder =
    loadingPlaceholder || t("cars:selectors.engineType.loading");
  const defaultEmptyText = emptyText || t("cars:selectors.engineType.empty");

  // Create a map for quick engine type lookup by ID
  const engineTypeMap = useMemo(() => {
    const map = new Map<number, CarEngineType>();
    engineTypes.forEach((engineType) => {
      if (engineType.id !== undefined) {
        map.set(engineType.id, engineType);
      }
    });
    return map;
  }, [engineTypes]);

  // Filter and prepare engine type options
  const engineTypeOptions = useMemo((): EngineTypeOptionData[] => {
    let filteredTypes = engineTypes;

    // Apply custom filter if provided
    if (engineTypeFilter) {
      filteredTypes = filteredTypes.filter(engineTypeFilter);
    }

    // Convert to select options
    return filteredTypes.map((engineType) => ({
      value: String(engineType.id),
      label: engineType.name || "Unknown",
      code: engineType.code,
      iconName: getEngineTypeIcon(engineType.code),
      color: getEngineTypeColor(engineType.code),
    }));
  }, [engineTypes, engineTypeFilter]);

  // Get selected engine type for icon display
  const selectedEngineType = useMemo(() => {
    if (value === null || value === undefined) return null;
    const numericValue =
      typeof value === "string" ? parseInt(value, 10) : value;
    return (
      engineTypeMap.get(numericValue) || getCarEngineTypeById(numericValue)
    );
  }, [value, engineTypeMap, getCarEngineTypeById]);

  // Handle onChange with engine type object lookup
  const handleChange = useCallback(
    (selectedValue: string | null) => {
      if (selectedValue) {
        const numericId = parseInt(selectedValue, 10);
        const engineType =
          engineTypeMap.get(numericId) || getCarEngineTypeById(numericId);
        onChange?.(name, numericId, engineType);
      } else {
        onChange?.(name, null, undefined);
      }
    },
    [name, engineTypeMap, getCarEngineTypeById, onChange]
  );

  // Render option for dropdown
  const renderOption: SelectProps["renderOption"] = useCallback(
    ({ option }: any) => {
      const optionData = option as EngineTypeOptionData;
      return (
        <Group gap="sm" wrap="nowrap">
          <AppIcon
            iconName={optionData.iconName}
            size={20}
            color={`var(--mantine-color-${optionData.color}-6)`}
          />
          <Text size="md" fw={500}>
            {optionData.label}
          </Text>
          {optionData.code && (
            <Badge size="md" variant="light" color={optionData.color}>
              {optionData.code}
            </Badge>
          )}
        </Group>
      );
    },
    []
  );

  // Memoize left section icon
  const leftSection = useMemo(() => {
    if (!selectedEngineType) return undefined;
    return (
      <AppIcon
        iconName={getEngineTypeIcon(selectedEngineType.code)}
        size={18}
        color={`var(--mantine-color-${getEngineTypeColor(
          selectedEngineType.code
        )}-6)`}
      />
    );
  }, [selectedEngineType]);

  // Determine current state
  const isEmpty = !isLoading && engineTypes.length === 0;

  // Memoize select state
  const selectState = useMemo(() => {
    if (isLoading && engineTypes.length === 0) {
      return {
        data: [] as EngineTypeOptionData[],
        placeholder: defaultLoadingPlaceholder,
        isDisabled: true,
        showLoading: true,
        errorMessage: undefined as string | undefined,
      };
    }

    if (lastError && !isLoading && engineTypes.length === 0) {
      return {
        data: [] as EngineTypeOptionData[],
        placeholder: t("common:errors.generic"),
        isDisabled: true,
        showLoading: false,
        errorMessage: error || t("common:messages.loadError"),
      };
    }

    if (isEmpty) {
      return {
        data: [] as EngineTypeOptionData[],
        placeholder: defaultEmptyText,
        isDisabled: true,
        showLoading: false,
        errorMessage: undefined as string | undefined,
      };
    }

    return {
      data: engineTypeOptions,
      placeholder: defaultPlaceholder,
      isDisabled: disabled || isLoading,
      showLoading: isLoading && engineTypes.length > 0,
      errorMessage: undefined as string | undefined,
    };
  }, [
    isLoading,
    engineTypes.length,
    lastError,
    isEmpty,
    engineTypeOptions,
    defaultLoadingPlaceholder,
    defaultEmptyText,
    defaultPlaceholder,
    disabled,
    error,
    t,
  ]);

  // Memoize right section
  const rightSection = useMemo(() => {
    if (selectState.showLoading) {
      return <LoadingIndicator size={size} color="gray" />;
    }
    return selectProps.rightSection;
  }, [selectState.showLoading, size, selectProps.rightSection]);

  return (
    <Box>
      <Select
        {...selectProps}
        label={defaultLabel}
        size={size}
        value={value !== null && value !== undefined ? String(value) : null}
        onChange={handleChange}
        placeholder={selectState.placeholder}
        clearable={!required}
        required={required}
        allowDeselect={!required}
        disabled={selectState.isDisabled}
        error={selectState.errorMessage || error}
        data={selectState.data}
        rightSection={rightSection}
        leftSection={leftSection}
        renderOption={renderOption}
        nothingFoundMessage={t("common:form.noOptions")}
        comboboxProps={DRAWER_COMBOBOX_PROPS}
      />
    </Box>
  );
};

export const CarEngineTypeSelector = React.memo(CarEngineTypeSelectorComponent);
